import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import i18n from '../i18n/config';

export type Language = 'en' | 'es' | 'es-MX' | 'es-CL' | 'es-EC' | 'pt';

const SUPPORTED_LANGUAGES: Language[] = ['en', 'es', 'es-MX', 'es-CL', 'es-EC', 'pt'];

interface LanguageContextType {
  currentLanguage: Language;
  changeLanguage: (language: Language) => Promise<void>;
  syncWithOrganization: () => Promise<void>;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>('en');
  const [isLoading, setIsLoading] = useState(true);

  const changeLanguage = async (language: Language) => {
    setCurrentLanguage(language);
    await i18n.changeLanguage(language);
    localStorage.setItem('catax-language', language);
  };

  /**
   * Fetch the organization (or effective user) settings from the backend
   * and apply the language returned. This should be called once the user
   * is authenticated so the org-level language overrides any local preference.
   */
  const syncWithOrganization = async () => {
    try {
      // Dynamic import to avoid a circular dependency at module load time
      const { apiService } = await import('../services/ApiService');
      const response = await apiService.get('/api/user/settings/settings');
      const orgLanguage: string = response.data?.settings?.language;
      if (orgLanguage && SUPPORTED_LANGUAGES.includes(orgLanguage as Language)) {
        await changeLanguage(orgLanguage as Language);
      }
    } catch (error) {
      // Non-fatal: fall back to whatever language is already active
      console.warn('Could not sync language from organization settings:', error);
    }
  };

  useEffect(() => {
    const initializeLanguage = async () => {
      const savedLanguage = localStorage.getItem('catax-language') as Language;
      if (savedLanguage && SUPPORTED_LANGUAGES.includes(savedLanguage)) {
        await changeLanguage(savedLanguage);
      } else {
        // Detect browser language
        const browserLang = navigator.language.split('-')[0];
        const supportedLang = SUPPORTED_LANGUAGES.includes(browserLang as Language)
          ? (browserLang as Language)
          : 'en';
        await changeLanguage(supportedLang);
      }
      setIsLoading(false);
    };

    initializeLanguage();
  }, []);

  const value = {
    currentLanguage,
    changeLanguage,
    syncWithOrganization,
    isLoading,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
